/**
 * Centralized API configuration and auth helpers.
 *
 * Every component that talks to the backend imports API_BASE_URL from here
 * so the host only needs to be changed in one place.
 */

const getDefaultApiBaseUrl = () => {
  const fromEnv = String(process.env.REACT_APP_API_URL || "")
    .trim()
    .replace(/\/$/, "");
  if (fromEnv) return fromEnv;

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      // Local uvicorn: 8000. Docker compose publishes the same API on 5040.
      return "http://127.0.0.1:8000";
    }
  }

  return "https://api.alsdnk.com";
};

export const API_BASE_URL = getDefaultApiBaseUrl();

export const SERVER_UNAVAILABLE_MESSAGE =
  "The school server is temporarily unavailable. Please try again in a moment.";

export const isServerUnavailableStatus = (status) =>
  status === 502 || status === 503 || status === 504;

export const readJsonSafe = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

export const apiErrorMessage = (response, fallback = "Request failed") => {
  if (isServerUnavailableStatus(response?.status)) {
    return SERVER_UNAVAILABLE_MESSAGE;
  }
  return fallback;
};

const AUTH_STORAGE_KEYS = [
  "als_token",
  "als_role",
  "als_teacher_id",
  "als_teacher_name",
  "als_teacher_campus_id",
  "als_teacher_email",
  "als_teacher_subject",
  "als_teacher_employee_id",
  "als_parent_id",
  "als_parent_name",
  "als_user_name",
];

/** Return a cleaned JWT from localStorage, or null if missing/invalid. */
export const getStoredToken = () => {
  const raw = localStorage.getItem("als_token");
  if (!raw) return null;
  const token = String(raw).trim();
  if (!token || token === "null" || token === "undefined") return null;
  return token;
};

/** Return the Authorization header using the stored JWT. */
export const getAuthHeaders = () => {
  const token = getStoredToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
};

/** Return Authorization + JSON Content-Type headers. */
export const getJsonAuthHeaders = () => ({
  "Content-Type": "application/json",
  ...getAuthHeaders(),
});

/** Clear all auth-related localStorage keys. */
export const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * Handle a 401 from teacher/admin APIs: clear session and send user to login.
 * Prefer passing onLogout from App when available.
 */
export const handleUnauthorized = (onLogout) => {
  clearAuthStorage();
  if (typeof onLogout === "function") {
    onLogout();
    return;
  }
  if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
    window.location.assign("/login");
  }
};

/**
 * fetch wrapper that attaches auth headers and redirects on 401.
 */
export const apiFetch = async (url, options = {}, onLogout) => {
  const headers = {
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    handleUnauthorized(onLogout);
  }
  return response;
};
